import { useEffect, useState, type ReactNode } from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { isSupportedLocale, pickDefaultLocale } from "@/i18n/locale-utils";
import NotFound from "@/pages/NotFound";

/**
 * Gates a route by app_role. Uses the public.has_role(uuid, app_role) RPC.
 */
export function RequireRole({
  role,
  children,
  onDeny = "redirect",
}: {
  role: "admin" | "provider" | "learner";
  children: ReactNode;
  onDeny?: "redirect" | "notFound";
}) {
  const [state, setState] = useState<"loading" | "ok" | "denied" | "anon">("loading");
  const { pathname, search, hash } = useLocation();
  const { locale: paramLocale } = useParams<{ locale: string }>();
  const locale = isSupportedLocale(paramLocale) ? paramLocale : pickDefaultLocale();

  useEffect(() => {
    let mounted = true;
    (async () => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) {
        if (mounted) setState("anon");
        return;
      }
      const { data, error } = await supabase.rpc("has_role", { _user_id: u.user.id, _role: role });
      if (!mounted) return;
      setState(!error && data === true ? "ok" : "denied");
    })();
    return () => {
      mounted = false;
    };
  }, [role]);

  if (state === "loading") {
    return (
      <div className="grid min-h-screen place-items-center text-sm text-muted-foreground">Loading…</div>
    );
  }
  if (onDeny === "notFound" && (state === "anon" || state === "denied")) {
    return <NotFound />;
  }
  if (state === "anon") {
    const next = encodeURIComponent(`${pathname}${search}${hash}`);
    return <Navigate to={`/${locale}/login?next=${next}`} replace />;
  }
  if (state === "denied") return <Navigate to={`/${locale}`} replace />;
  return <>{children}</>;
}
